import { original, type PayloadAction } from '@reduxjs/toolkit';
import type { ISchedulerData, SiteId, TaskId } from '@interfaces/IScheduler';
import {
  splitScheduleData,
  type ISchedulerPlanData,
  type ISchedulerPlanTask,
} from '@components/Pages/SchedulerPage/lib/taskViewCodec';
import type { CascadeFreeze } from '@components/Pages/SchedulerPage/lib/freeze';
import type { ISchedulerState } from './scheduler.state';
import { diffTree, MERGE, replayDelta, type TreeDelta } from './scheduler.delta';

export function bootstrap(state: ISchedulerState, action: PayloadAction<ISchedulerData>) {
  // Split the fetched TaskViews into the two facets: plan tasks into
  // `bySite`, execution records into `activityBySite`. After bootstrap the
  // execution map is authoritative between loads.
  const { plan, executionByTaskId } = splitScheduleData(action.payload);
  state.bySite[action.payload.siteId] = plan;
  state.activityBySite[action.payload.siteId] = executionByTaskId;
  state.dirtyBySite[action.payload.siteId] = false;
  state.staleBySite[action.payload.siteId] = false;
}

/**
 * Take the server's execution facet over the page's — after the server refused
 * a write the page had already applied (a tick an amendment removed). Between
 * loads the local execution map is authoritative, so nothing else would undo it.
 */
export function executionRefreshed(state: ISchedulerState, action: PayloadAction<{ siteId: SiteId; fresh: ISchedulerData }>) {
  state.activityBySite[action.payload.siteId] = splitScheduleData(action.payload.fresh).executionByTaskId;
}

/** Mark the loaded schedule as behind/at the upstream version (freezes edits while stale). */
export function setStale(state: ISchedulerState, action: PayloadAction<{ siteId: SiteId; stale: boolean }>) {
  state.staleBySite[action.payload.siteId] = action.payload.stale;
}

/**
 * Mirror the observed-work anchors in from the committed-activities feed, which
 * the cascade needs and cannot reach (2026-09-20 amendment, decision 3). Not a
 * data mutation — it reports what the field has been seen doing, so it is in
 * none of UNDOABLE / SYNCABLE / FREEZABLE and still lands on a stale base.
 */
export function setFrozenTasks(
  state: ISchedulerState,
  action: PayloadAction<{ siteId: SiteId; frozen: Record<TaskId, CascadeFreeze> }>,
) {
  state.frozenBySite[action.payload.siteId] = action.payload.frozen;
}

/**
 * Force the site dirty. Used as the fallback when an immediate-write surface
 * (the Locations tab's per-task persist) fails to reach the backend — lighting
 * the Save button so the whole-doc PUT can recover the change. Not undoable /
 * not itself syncable (it only flips the flag the HOR otherwise owns).
 */
export function markDirty(state: ISchedulerState, action: PayloadAction<{ siteId: SiteId }>) {
  state.dirtyBySite[action.payload.siteId] = true;
}

/**
 * The server took one of this client's per-task plan writes and it was the
 * only write since the version the store holds: move the store's version token
 * to it, so the stale probe doesn't read our own write as another editor's.
 */
export function planVersionAcknowledged(state: ISchedulerState, action: PayloadAction<{ siteId: SiteId; version: string }>) {
  const site = state.bySite[action.payload.siteId];
  if (site) site.version = action.payload.version;
}

/**
 * Re-apply the user's unsaved edits — `edits`, a delta from some other copy to
 * the working copy — on top of `onto`, the user's edit winning field by field.
 * The version token is concurrency, not content: `onto`'s stays. A dependency
 * whose endpoint is no longer in the result (the other copy deleted it) goes.
 */
function replayEdits(onto: ISchedulerPlanData, edits: TreeDelta): ISchedulerPlanData {
  const merged = { ...(replayDelta(onto, edits, MERGE) as ISchedulerPlanData), version: onto.version };
  const taskIds = new Set(merged.tasks.map((task) => task.id));
  const live = merged.dependencies.filter((dep) => taskIds.has(dep.predecessorId) && taskIds.has(dep.successorId));
  return live.length === merged.dependencies.length ? merged : { ...merged, dependencies: live };
}

/** The user's edits from `from` to `working`, the version token aside. */
function editsSince(from: ISchedulerPlanData, working: ISchedulerPlanData): TreeDelta | null {
  return diffTree({ ...from, siteId: working.siteId, version: working.version }, working);
}

/**
 * Plan fields a task the server already has writes through its own endpoint —
 * status (row actions, finish), areas, and the drawer's auto-saved profession
 * and crew size. They are never an unsaved edit: the write went out on its own
 * (or was refused, and the page is being put back), so the server's copy is the
 * truth. A task the server doesn't have yet keeps them — they ride its Save.
 */
const IMMEDIATE_TASK_FIELDS = ['status', 'locations', 'profession', 'workerCount'] as const;

/** `working` with every saved task's immediate-write fields as `saved` has them. */
function withSavedImmediateFields(working: ISchedulerPlanData, saved: ISchedulerPlanData): ISchedulerPlanData {
  const savedById = new Map(saved.tasks.map((task) => [task.id, task]));
  return {
    ...working,
    tasks: working.tasks.map((task) => {
      const savedTask = savedById.get(task.id);
      if (!savedTask) return task;
      const next: Record<string, unknown> = { ...task };
      for (const field of IMMEDIATE_TASK_FIELDS) {
        if (field in savedTask) next[field] = savedTask[field];
        else delete next[field];
      }
      return next as unknown as ISchedulerPlanTask;
    }),
  };
}

/** Drop a selection / open panel whose task is not in the new working copy. */
function dropMissingSelection(state: ISchedulerState, tasks: readonly ISchedulerPlanTask[]) {
  const taskIds = new Set(tasks.map((task) => task.id));
  if (state.ui.selectedTaskId && !taskIds.has(state.ui.selectedTaskId)) {
    state.ui.selectedTaskId = null;
  }
  if (state.ui.panelOpen?.taskId && !taskIds.has(state.ui.panelOpen.taskId)) {
    state.ui.panelOpen = null;
  }
}

/**
 * Replace a site's data with the post-save server response. D-ORCA now OWNS
 * the schedule and returns the authoritative full document with STABLE ids
 * (and every persisted field — checklist, comments, markers, trade…), so
 * there's no Excel re-parse, no id re-synthesis, and no (group name, task
 * name) remapping / client-local carry-over to do. We trust the server doc
 * verbatim; only the local view state (group collapse) is preserved, and a
 * selection/panel that points at a task the server no longer has is cleared.
 * History ends here (the slice's DOC_REPLACING).
 *
 * `sent` is the working copy the PUT carried. Edits made while it was in
 * flight are the delta from `sent` to the working copy now; they are replayed
 * on the server doc and keep the site dirty, instead of vanishing under it
 * (QA F-70). Without `sent` the server doc is taken as is.
 *
 * Only the PLAN half of the response is applied. The server also echoes
 * execution (composed `TaskView`s), but the local `activityBySite` map is
 * authoritative post-bootstrap (the serialized per-task PUT channel
 * guarantees server convergence), so the execution half is DISCARDED — this
 * is what deletes the old immediate-write reconcile overlay.
 */
export function reconcileSaved(
  state: ISchedulerState,
  action: PayloadAction<{ siteId: SiteId; data: ISchedulerData; sent?: ISchedulerPlanData }>,
) {
  const { siteId, data, sent } = action.payload;
  const prevDraft = state.bySite[siteId];
  const prev = prevDraft && original(prevDraft);
  const { plan } = splitScheduleData(data);
  let next: ISchedulerPlanData = { ...plan, siteId };

  if (prev) {
    // Group collapse is pure local view state (not server-owned) — carry it
    // over by id (stable now).
    const collapsedById = new Map(prev.groups.map((g) => [g.id, g.collapsed]));
    next.groups = next.groups.map((g) =>
      collapsedById.has(g.id) ? { ...g, collapsed: collapsedById.get(g.id)! } : g,
    );
    const prevTaskById = new Map(prev.tasks.map((t) => [t.id, t]));
    next.tasks = next.tasks.map((t) => {
      const prevTask = prevTaskById.get(t.id);
      // Task collapse is the same kind of view state as group collapse —
      // carry it over so a saved sub-tree doesn't silently re-expand.
      if (prevTask?.collapsed) return { ...t, collapsed: prevTask.collapsed };
      return t;
    });
  }

  const inFlight = prev && sent ? editsSince(sent, prev) : null;
  if (inFlight) next = replayEdits(next, inFlight);

  dropMissingSelection(state, next.tasks);
  state.bySite[siteId] = next;
  state.dirtyBySite[siteId] = inFlight !== null;
  state.staleBySite[siteId] = false;
}

/**
 * Rebase: replace the working copy with the freshly-fetched D-ORCA doc and
 * RE-APPLY the user's unsaved edits on top — so a 409 SCHEDULE_CONFLICT
 * (a concurrent D-ORCA editor saved first) or a stale-copy Load never
 * discards work.
 *
 * The unsaved edits are the delta from `baseline` (the copy the working copy
 * started from) to the working copy, keyed by the STABLE ids
 * (`scheduler.delta.ts`), and they are replayed on the fresh doc whole:
 * - every field the user changed on a task, group, dependency or marker —
 *   name, dates, a dependency's type and lag, a checklist (one field), …
 *   (field-level last-writer-wins — the user reviews before re-saving) —
 *   except a saved task's immediate-write fields, which take the fresh value
 *   (IMMEDIATE_TASK_FIELDS: that is also how a refused write is put back);
 * - everything the user ADDED (re-added unless fresh has it, in place);
 * - everything the user DELETED — tasks, groups, dependencies, markers — stays
 *   deleted (QA F-71: a deleted task used to come back while its dependency
 *   removals stuck, a half-applied delete);
 * - everything untouched takes the fresh upstream value; a dependency left
 *   without an endpoint is dropped.
 * Dirty reflects whether anything was re-applied; stale is cleared. History
 * ends here (the slice's DOC_REPLACING).
 *
 * PLAN-only: `fresh`/`baseline` are split to their plan facet and only plan
 * fields are merged. Execution (progress/log/comments/…) is never versioned or
 * rebased — the local `activityBySite` map wins and is left untouched, so the
 * whole "Save clobbers immediate writes" class stays structurally impossible.
 */
export function rebaseOnFresh(
  state: ISchedulerState,
  action: PayloadAction<{ siteId: SiteId; fresh: ISchedulerData; baseline?: ISchedulerData }>,
) {
  const { siteId, fresh, baseline } = action.payload;
  const editedDraft = state.bySite[siteId];
  const edited = editedDraft && original(editedDraft);
  const freshPlan: ISchedulerPlanData = { ...splitScheduleData(fresh).plan, siteId };
  const baselinePlan = baseline && splitScheduleData(baseline).plan;
  const unsaved = edited && baselinePlan ? editsSince(baselinePlan, withSavedImmediateFields(edited, baselinePlan)) : null;
  const next = unsaved ? replayEdits(freshPlan, unsaved) : freshPlan;

  dropMissingSelection(state, next.tasks);
  state.bySite[siteId] = next;
  state.dirtyBySite[siteId] = unsaved !== null;
  state.staleBySite[siteId] = false;
}
